import { HTMLAttributes, useState } from "react";

import { cn } from "@/lib/utils";
import { useNavigate } from "@tanstack/react-router";
import { Home, ArrowDownToLine, TreePine, Wrench, Boxes, Loader2 } from "lucide-react";
import { CommonInstallflowFrame } from "./common-install-flow-frame";
import { apiClient } from "@/api/client";
import { SublocationData } from "@/sublocation/sublocation";
import {
  installFlowOrganizationId,
  installFlowLocationId,
  installFlowSublocationId,
  installFlowSublocationName,
} from "@/installFlow/storage-keys";

const getInstallFlowOrgId = (): number =>
  Number(installFlowOrganizationId.get());

const LOCATION_OPTIONS = [
  { id: "Rooftop",        label: "Rooftop",         icon: Home },
  { id: "Basement",       label: "Basement",        icon: ArrowDownToLine },
  { id: "Outdoor",        label: "Outdoor",         icon: TreePine },
  { id: "Mechanical Room",label: "Mechanical Room", icon: Wrench },
  { id: "Elevator Room",  label: "Elevator Room",   icon: Boxes },
] as const;

interface AuthProps extends HTMLAttributes<HTMLDivElement> {}

export function OnSiteLocation({ className }: AuthProps) {
  const navigate = useNavigate();
  const orgId = getInstallFlowOrgId();
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * Creates a sublocation via the API then proceeds to the nameplate photo step.
   *
   * Required sessionStorage keys (set by earlier steps):
   *   - "installFlowLocationId"   — numeric ID of the location selected in asset-location.tsx
   *
   * What it stores on success:
   *   - "installFlowSublocationId"   — the new sublocation's ID
   *   - "installFlowSublocationName" — the new sublocation's name
   */
  async function handleSelect(sublocationName: string) {
    setError(null);
    setSaving(sublocationName);

    const locationId = installFlowLocationId.get();
    if (!locationId) {
      setError("Location not found. Please go back and select a location first.");
      setSaving(null);
      return;
    }

    try {
      const res = await apiClient.post<SublocationData>(
        `/organizations/${orgId}/locations/${locationId}/sublocations/`,
        { name: sublocationName, location: Number(locationId) },
      );

      installFlowSublocationId.set(String(res.data.id));
      installFlowSublocationName.set(res.data.name);

      await navigate({ to: "/installFlow/name-plate-photo" });
    } catch {
      setError("Failed to save location. Please try again.");
    } finally {
      setSaving(null);
    }
  }

  return (
    <CommonInstallflowFrame>
      <div className="w-full max-w-md flex flex-col gap-6 pt-6">
        <div className="text-left space-y-2">
          <h1 className="text-xl font-bold text-black leading-[26px]">
            Where is this asset?
          </h1>
          <p className="text-base text-[#727272] leading-[20px] font-semibold">
            This will help your team find the asset when on site.
          </p>
        </div>

        {error && (
          <p className="text-sm font-medium text-red-600" role="alert">
            {error}
          </p>
        )}

        <div className="flex flex-col gap-3">
          {LOCATION_OPTIONS.map(({ id, label, icon: Icon }) => {
            const isSaving = saving === id;
            return (
              <button
                key={id}
                type="button"
                disabled={saving !== null}
                onClick={() => handleSelect(id)}
                className={cn(
                  "flex items-center gap-4 w-full rounded-xl px-4 py-3 text-left transition-colors",
                  "bg-[#E7F3FF] hover:bg-sky-100 border",
                  saving !== null && "opacity-60 cursor-not-allowed",
                )}
              >
                {isSaving ? (
                  <Loader2 className="h-6 w-6 shrink-0 text-sky-700 animate-spin" />
                ) : (
                  <Icon className="h-6 w-6 shrink-0 text-sky-700" aria-hidden />
                )}
                <span className="font-medium text-foreground">{label}</span>
              </button>
            );
          })}

          <button
            type="button"
            disabled={saving !== null}
            onClick={() => handleSelect("Custom")}
            className={cn(
              "flex items-center gap-4 w-full rounded-xl px-4 py-3 text-left transition-colors",
              "bg-[#EDF0F3] border border-border",
              saving !== null && "opacity-60 cursor-not-allowed",
            )}
          >
            {saving === "Custom" ? (
              <Loader2 className="h-5 w-5 shrink-0 text-gray-500 animate-spin" />
            ) : null}
            <span className="font-semibold leading-[20px] text-black">Custom</span>
          </button>
        </div>
      </div>
    </CommonInstallflowFrame>
  );
}
